import GlobalStore from '../datastorage/GlobalStore';
import DeviceDetector from '../helper/DeviceDetector';
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';

import ScrollSmoother from 'gsap/dist/ScrollSmoother';
import { EventDispatcher } from './EventDispatcher';

import { getApp } from '../main.js';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

export default class ContentAnimationController {
    constructor() {
        this._topBar = document.getElementById('top-bar');
        this._isTopBarHidden = false;

        this.appColorMap = {
            white: 'var(--col-bg-app-bright)',
            black: 'var(--col-bg-app-dark)',
        };
        this.currentAppColor = this.appColorMap['white'];

        this._isMobile = window.innerWidth <= 768;

        this.cachedWidth = window.innerWidth;
        this.cachedHeight = window.innerHeight;

        this.setupPageListeners();

        this.initSmoothScroll();
    }

    setupPageListeners() {
        EventDispatcher.on('pageTransition', this.handlePageTransition.bind(this));
        EventDispatcher.on('pageResize', this.onWindowResize.bind(this), false);
    }

    handlePageTransition(data) {
        if (data.detail.namespace === 'after' || data.detail.namespace === 'once') {
            this.refreshSmoothScrollAndTrigger();
            this.checkContentAnimation();
            this.handleTopBar(true);
            this.smoothScrollPaused(false);
        }

        if (data.detail.namespace === 'enter' || data.detail.namespace === 'once') {
            if (this.currentAppColor !== this.appColorMap[data.detail.container.dataset.barbaBgcolour]) {
                this.setPageColour(data);
            }
        }
        if (data.detail.namespace === 'leave') {
            this.smoothScrollPaused(true);
        }
        if (data.detail.namespace === 'afterLeave') {
            this.disposeScrollTriggers();
        }
    }

    animateDescriptionStats(holder) {
        const arrStatsCards = holder.querySelectorAll('[data-animate="stats-cards"]');

        arrStatsCards.forEach((card, index) => {
            gsap.fromTo(
                card,
                { opacity: 0 },
                {
                    opacity: 1,
                    duration: 0.45,
                    ease: 'power2.out',
                    delay: this.isMobileScroll() ? 0 : index * 0.2,
                    scrollTrigger: {
                        trigger: card,
                        start: this.isMobileScroll() ? 'top+=50 bottom' : 'top+=180 bottom',
                        toggleActions: 'play none none none',
                    },
                }
            );
        });
    }

    animateCTAPage(holder) {
        const arrTeaserCards = holder.querySelectorAll('[data-animate="card-teaser"]');

        arrTeaserCards.forEach((card, index) => {
            const image = card.querySelector('.content-image');
            gsap.fromTo(
                card,
                { autoAlpha: 0 },
                {
                    autoAlpha: 1,
                    duration: 0.45,
                    ease: 'power2.out',
                    delay: this.isMobileScroll() ? 0 : index * 0.2,
                    scrollTrigger: {
                        trigger: card,
                        start: this.isMobileScroll() ? 'top+=50 bottom' : 'top+=180 bottom',
                        toggleActions: 'play none none none',
                    },
                    onStart: () => {
                        console.log('START PAGE TEASER ANIME');
                    },
                }
            );

            gsap.fromTo(
                image,
                { scale: 1.2, autoAlpha: 0 },
                {
                    scale: 1,
                    autoAlpha: 1,
                    duration: 1,
                    ease: 'power2.out',
                    delay: this.isMobileScroll() ? 0 : index * 0.2,
                    scrollTrigger: {
                        trigger: image,
                        start: this.isMobileScroll() ? 'top+=50 bottom' : 'top+=150 bottom',
                        toggleActions: 'play none none none',
                    },
                }
            );
        });
    }

    animateImagesText(holder) {
        let arrImages = holder.querySelectorAll('.content-image');

        arrImages = Array.from(arrImages);

        if (holder.classList.contains('images-text-imagealignment-right')) {
            arrImages.reverse();
        }

        arrImages.forEach((image, index) => {
            gsap.fromTo(
                image,
                { y: (this.isMobileScroll() ? 100 : 450) * index },
                {
                    y: -100 * (index + 1),
                    ease: 'none',
                    scrollTrigger: {
                        trigger: holder,
                        start: this.isMobileScroll() ? 'top bottom' : 'top+=150 bottom',
                        end: this.isMobileScroll() ? 'bottom+=80 center' : 'bottom+=100 center',
                        scrub: 0.95,
                    },
                }
            );
        });
    }

    animateHeaderImage(holder) {
        const holderContent = holder.querySelector('[data-animate="header-text"]');
        const tl_Header = gsap.timeline();

        tl_Header
            .fromTo(
                holderContent.querySelector('h1'),
                { scale: 0.8, autoAlpha: 0 },
                {
                    scale: 1,
                    autoAlpha: 1,
                    duration: 0.65,
                    ease: 'power2.out',
                }
            )
            .fromTo(
                holderContent.querySelector('h2'),
                { autoAlpha: 0, y: 20 },
                {
                    y: 0,
                    autoAlpha: 1,
                    duration: 0.65,
                    ease: 'power2.out',
                },
                '-=0.4'
            );
    }

    animateFeatureCards(holder) {
        const arrFeatureCards = holder.querySelectorAll('[data-animate="feature-cards"]');

        arrFeatureCards.forEach((card, index) => {
            gsap.fromTo(
                card,
                { opacity: 0 },
                {
                    opacity: 1,
                    duration: 0.45,
                    ease: 'power2.out',
                    delay: this.isMobileScroll() ? 0 : index * 0.2,
                    scrollTrigger: {
                        trigger: card,
                        start: this.isMobileScroll() ? 'top+=50 bottom' : 'top+=180 bottom',
                        toggleActions: 'play none none none',
                    },
                }
            );
        });
    }

    animateDescriptionTutorial(holder) {
        const arrTutorials = holder.querySelectorAll('[data-animate="tutorial-cards"]');

        arrTutorials.forEach((image) => {
            let img = image.querySelector('.content-image');

            gsap.fromTo(
                img,
                { y: 350 },
                {
                    y: 0,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: image,
                        start: this.isMobileScroll() ? 'top bottom' : 'top+=150 bottom',
                        end: this.isMobileScroll() ? 'top center' : 'center center',
                        scrub: 0.95,
                    },
                }
            );
        });
    }

    animateCopyLong(holder) {
        const holderCopyLong = holder.querySelector('[data-animate="copy-long"]');
        gsap.fromTo(
            holderCopyLong,
            { autoAlpha: 0 },
            {
                autoAlpha: 1,
                ease: 'none',
                scrollTrigger: {
                    trigger: holderCopyLong,
                    start: this.isMobileScroll() ? 'top bottom' : 'top+=150 bottom',
                    end: this.isMobileScroll() ? 'top-=50 center' : 'top-=150 center',
                    scrub: 0.95,
                },
            }
        );
    }

    animateInfoCards(holder) {
        const arrInfo = holder.querySelectorAll('[data-animate="info-cards"]');

        arrInfo.forEach((card, index) => {
            gsap.fromTo(
                card,
                { y: this.isMobileScroll() ? 80 : 20 * (index + 1) },
                {
                    y: 0,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: card,
                        start: this.isMobileScroll() ? 'top bottom' : 'top bottom',
                        end: this.isMobileScroll() ? 'top-=50 center' : 'top-=300 center',
                        scrub: 0.95,
                    },
                }
            );
        });
    }

    animateFullscreenImageText(holder) {
        const fsImageText = holder.querySelector('[data-animate="fs-image-text"]');

        gsap.fromTo(
            fsImageText.querySelector('h2'),
            { scale: 1.5 },
            {
                scale: 1,
                ease: 'none',
                scrollTrigger: {
                    trigger: holder,
                    start: this.isMobileScroll() ? 'top bottom' : 'top+=150 bottom',
                    end: this.isMobileScroll() ? 'top center' : 'top center',
                    scrub: 1.85,
                },
            }
        );
    }

    checkContentAnimation() {
        document.querySelectorAll('[data-animate-name]').forEach((el) => {
            const fn = el.dataset.animateName;

            if (typeof this[fn] === 'function') {
                this[fn](el);
            }
        });
    }

    refreshSmoothScrollAndTrigger() {
        const smoother = ScrollSmoother.get();
        if (smoother) {
            ScrollTrigger.refresh();
            smoother.scrollTo(0, false);
            smoother.refresh();
        }
    }

    disposeScrollTriggers() {
        if (ScrollTrigger.getAll().length) {
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        }
    }

    onWindowResize() {
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;

        this.isResizing = false;

        const isMobile = 'ontouchstart' in window && newWidth <= 1024;

        if (isMobile) {
            if (newWidth !== this.cachedWidth) {
                this.isResizing = true;
                clearTimeout(this._resizeTimeout);
                this._resizeTimeout = setTimeout(() => {
                    this.isResizing = false;
                    this.handleTopBar(true);
                }, 500);
                if (ScrollTrigger.getAll().length > 0) {
                    this.disposeScrollTriggers();
                    this.refreshSmoothScrollAndTrigger();
                    this.checkContentAnimation();
                }

                this.cachedWidth = newWidth;
                this.cachedHeight = newHeight;
            }
        } else {
            this.isResizing = true;
            clearTimeout(this._resizeTimeout);
            this._resizeTimeout = setTimeout(() => {
                this.isResizing = false;
                this.handleTopBar(true);
            }, 500);
            if (newWidth !== this.cachedWidth || newHeight !== this.cachedHeight) {
                if (ScrollTrigger.getAll().length > 0) ScrollTrigger.refresh();

                this.cachedWidth = newWidth;
                this.cachedHeight = newHeight;
            }
        }
    }

    handleTopBar(reset = false) {
        if (!this._topBar || !this.myScroll) return;

        if (reset) {
            // gsap.killTweensOf(this._topBar);
            gsap.set(this._topBar, { y: 0, autoAlpha: 1 });
            this._isTopBarHidden = false;
            this._topBar.style.pointerEvents = 'auto';
            this.lastTopPos = null;

            return;
        }

        const currentY = this.myScroll.scrollTop();
        const direction = this.myScroll.getVelocity() > 0 ? 1 : -1;

        if (!this._isTopBarHidden) this._isTopBarHidden = false;

        if (direction === 1 && this.lastTopPos) this.lastTopPos = null;

        if (direction === 1 && currentY > 100 && !this._isTopBarHidden) {
            gsap.to(this._topBar, {
                y: '-6rem',
                autoAlpha: 0,
                duration: 0.45,
                ease: 'power2.in',
                // force3D: true,
                onStart: () => {
                    this._topBar.style.pointerEvents = 'none';
                    if (getApp().myMainNavigation.is_LogInFlyoutOpen) getApp().myMainNavigation.controllLogInFlyout();
                },
            });
            this._isTopBarHidden = true;
        } else if (direction === -1 && this._isTopBarHidden) {
            let topValue = window.innerWidth >= 1024 ? '0.875rem' : '0.5833rem';

            if (!this.lastTopPos) {
                this.lastTopPos = currentY;
            }

            if (currentY <= this.lastTopPos - 100) {
                gsap.to(this._topBar, {
                    y: 0,
                    autoAlpha: 1,
                    duration: 0.45,
                    ease: 'power2.out',
                    // force3D: true,
                    onComplete: () => {
                        this._topBar.style.pointerEvents = 'auto';
                    },
                });
                this.lastTopPos = null;
                this._isTopBarHidden = false;
            }
        }
    }

    initSmoothScroll() {
        this.myScroll = ScrollSmoother.create({
            wrapper: '#smooth-wrapper',
            content: '#smooth-content',
            smooth: 0.85,
            effects: true,
            //smoothTouch: 0.1,
            ignoreMobileResize: true,
            onUpdate: () => {
                if (!this.isResizing) {
                    this.handleTopBar();
                }
            },
        });
    }

    smoothScrollPaused(state) {
        this.myScroll.paused(state);
    }

    setPageColour(data) {
        const newContainer = data.detail.container;
        const colorKey = newContainer.dataset.barbaBgcolour || 'white';

        const newColor = this.appColorMap[colorKey] || this.appColorMap[white];

        this.currentAppColor = newColor;

        gsap.to('#smooth-wrapper', {
            backgroundColor: newColor,
            duration: 0.35,
            ease: 'power2.out',
        });
    }

    isMobileScroll() {
        return window.innerWidth < 767;
    }
}
